/**
 * Authored-identity hash for workflow nodes.
 *
 * The `.canary.md` grammar is deliberately a *human-intent* format: it can
 * express what a node is trying to do (titles, descriptions, fields,
 * assertions, navigation targets) but NOT the runtime artifacts a node
 * accumulates — `playbookSteps` (the recorded click-by-click replay), the
 * live `pageUrl` an action ran against, agent guidance, cache metadata. Those
 * are generated/recorded server-side and have no spec syntax.
 *
 * `authoredNodeHash` fingerprints only the *authorable* slice of a node's
 * `configJson` — the part a `.canary.md` author actually controls. The
 * recompile diff compares this hash (spec vs live) instead of the whole
 * `configJson`, so a node that round-tripped through `pull` → edit → `compile`
 * is reported as **changed only when its authored intent changed**. Untouched
 * nodes hash-match, the diff skips them, and their recorded playbook + cache
 * survive (see `sync.ts`, which only PUTs a node when `configChanged`).
 *
 * This is the spec-side sibling of the cache's node hash
 * (`apps/api/src/workflows/helpers/node-hash.ts` — in particular its
 * `hashNodeContextLegacy`, the playbook-excluded variant). It is reimplemented
 * here, not imported, because packages must not depend on `apps/*`.
 *
 * The projection + field helpers live in the Node-builtin-free
 * `./authored-fields.js` so they can be reused in a browser bundle (the spec
 * renderer); only the hashing itself needs `node:crypto`, which keeps this
 * module Node-only.
 *
 * @module spec/node-hash
 */

import { createHash } from 'node:crypto';
import { authoredProjection, sortObjectKeys, type HashableNode } from './authored-fields.js';

// Re-export the authored-fields surface so existing `./node-hash.js` imports
// (and the package barrel) keep resolving unchanged.
export type { HashableNode };
export {
  authoredProjection,
  authoredActionTitle,
  authoredActionFields,
  loginCredentialRef,
  sortObjectKeys,
} from './authored-fields.js';

/**
 * Deterministic hash of a node's authored identity. Applied identically to a
 * spec-emitted node and to a live workflow node — equal hashes mean "same
 * authored intent", regardless of runtime fields or key ordering.
 */
export function authoredNodeHash(node: HashableNode): string {
  const projection = {
    nodeType: node.nodeType,
    ...authoredProjection(node.nodeType, node.configJson),
  };
  const stable = JSON.stringify(sortObjectKeys(projection));
  return createHash('sha256').update(stable).digest('hex').slice(0, 16);
}
